import { useEffect, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { keepOpenForChips } from "@/components/ChipInput";
import { useNavigate } from "react-router-dom";
import { Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { WorkForm, workBody } from "@/components/WorkForm";
import { api, formatApiError } from "@/lib/api";
import { FONTS } from "@/lib/richtext";
import { useAppearance } from "@/context/AppearanceContext";
import { SceneBreakSample } from "@/components/SceneBreakCard";
import { Link } from "react-router-dom";
import { PAGE_SIZES } from "@/components/Paged";

const sel = "h-10 w-full rounded-md bg-inset border border-line text-sm text-slate-200 px-3";

const Memberships = ({ workId }) => {
  const [cols, setCols] = useState(null);
  useEffect(() => { api.get("/writing/collections").then((r) => setCols(r.data.filter((c) => c.kind === "manual"))); }, []);
  const toggle = async (c, add) => {
    try { const { data } = await api.post(`/writing/collections/${c.id}/works`, { work_id: workId, add }); setCols((l) => l.map((x) => (x.id === c.id ? data : x))); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!cols) return null;
  return (
    <div className="mt-8 space-y-2" data-testid="work-collections">
      <p className="text-sm text-slate-200">Hand-picked collections</p>
      {cols.length === 0 && <p className="text-xs text-slate-500">Make one from the Works page with "New collection".</p>}
      {cols.map((c) => (
        <label key={c.id} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
          <Checkbox checked={c.work_ids.includes(workId)} onCheckedChange={(on) => toggle(c, !!on)} data-testid={`work-collection-${c.id}`} />{c.name}
        </label>
      ))}
    </div>
  );
};

const SceneBreakField = ({ value, onChange }) => {
  const { app, design } = useAppearance();
  if (!app) return null;
  const options = app.scene_breaks.filter((d) => d.active || d.id === value);
  const shown = design(app.is_pro ? value || app.scene_break : "default");
  return (
    <div className="space-y-1.5 text-sm text-slate-200" data-testid="work-details-scene-break">
      <span>Scene break</span>
      <div className="flex items-center gap-3">
        <select value={app.is_pro ? value || "" : ""} disabled={!app.is_pro} onChange={(e) => onChange(e.target.value || null)} className={`${sel} flex-1 disabled:opacity-60`} data-testid="work-details-scene-break-select">
          <option value="">Account default ({design(app.is_pro ? app.scene_break : "default")?.name})</option>
          {app.is_pro && options.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <div className="w-28 shrink-0 rounded-lg bg-inset px-2">{shown && <SceneBreakSample d={shown} />}</div>
      </div>
      {!app.is_pro && <p className="text-[11px] text-slate-500">More scene break designs come with <Link to="/pricing" className="text-amber-300 hover:underline" data-testid="scene-break-upgrade-link">Pro</Link>.</p>}
    </div>
  );
};

export const WorkDetails = ({ work, onSaved }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(work);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if ((f.scene_break || null) !== (work.scene_break || null)) await api.put(`/writing/works/${work.id}/scene-break`, { scene_break: f.scene_break || null });
      const { data } = await api.put(`/writing/works/${work.id}`, workBody(f)); onSaved(data); toast.success("Details saved"); setOpen(false);
    }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/writing/works/${work.id}`); toast.success(`"${work.title}" was deleted`); nav("/writing"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(work); }}>
      <SheetTrigger asChild>
        <button className="inline-flex shrink-0 items-center gap-1.5 text-xs rounded-full border border-line px-3 py-1.5 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 transition-colors" data-testid="work-details-btn">
          <Settings2 className="w-3.5 h-3.5" /><span className="hidden sm:inline">Details</span>
        </button>
      </SheetTrigger>
      <SheetContent onEscapeKeyDown={keepOpenForChips} className="bg-panel border-line w-full sm:max-w-lg overflow-y-auto" data-testid="work-details-panel">
        <SheetTitle className="font-display text-3xl text-amber-100">Work details</SheetTitle>
        <SheetDescription className="text-slate-400 text-sm mb-5">Only you can see this work.</SheetDescription>
        <form onSubmit={save} className="space-y-5">
          <WorkForm value={f} onChange={setF} />
          <label className="flex items-center justify-between gap-3 rounded-xl border border-line p-4 text-sm text-slate-200">
            <span>On hiatus<span className="block text-[11px] text-slate-500">Overrides the automatic status until you turn it off.</span></span>
            <Switch checked={!!f.hiatus} onCheckedChange={(hiatus) => setF({ ...f, hiatus })} data-testid="work-details-hiatus-switch" />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1.5 text-sm text-slate-200"><span>Default font</span>
              <select value={f.font || ""} onChange={(e) => setF({ ...f, font: e.target.value })} style={{ fontFamily: f.font || undefined }} className={sel} data-testid="work-details-font">
                {FONTS.map(([l, v]) => <option key={l} value={v} style={{ fontFamily: v || undefined }}>{l === "Default" ? "Standard (Cormorant)" : l}</option>)}
              </select></label>
            <label className="space-y-1.5 text-sm text-slate-200"><span>Page size</span>
              <select value={f.page_size || "paperback"} onChange={(e) => setF({ ...f, page_size: e.target.value })} className={sel} data-testid="work-details-page-size">
                {PAGE_SIZES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></label>
          </div>
          <SceneBreakField value={f.scene_break} onChange={(scene_break) => setF({ ...f, scene_break })} />
          <button disabled={busy} className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold h-10" data-testid="work-details-save-btn">{busy ? "Saving…" : "Save details"}</button>
        </form>
        {open && <Memberships workId={work.id} />}
        <button onClick={() => setConfirm(true)} className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-10 text-sm" data-testid="work-delete-btn"><Trash2 className="w-4 h-4" /> Delete this work</button>
        <AlertDialog open={confirm} onOpenChange={setConfirm}>
          <AlertDialogContent className="bg-panel border-line" data-testid="work-delete-dialog">
            <AlertDialogTitle className="font-display text-2xl text-amber-50">Delete "{work.title}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Every chapter goes with it. This can't be undone.</AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="work-delete-cancel">Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-rose-500 hover:bg-rose-400 text-white" data-testid="work-delete-confirm">Delete work</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};
